import React, { useRef, useState } from 'react';
import {
  Controller,
  useForm,
} from 'react-hook-form';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import IntlTelInput from '@intl-tel-input/react';
import 'intl-tel-input/styles';

import { APIsignUp } from '../api/Auth';

import type { SignUpFormData } from '../types/forms/signupform';

const SignUp: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const phoneFieldRef = useRef<any>(null);

  const { mutate, isPending, isError } = useMutation({
    mutationFn: async (data: SignUpFormData) => {
      return await APIsignUp(data);
    },
    onError: (error) => {
      setErrorMessage(error.message || 'An error occurred during signing up.');
    },
    onSuccess: () => {
      setErrorMessage(null);
      setSuccess(true);
    },
  });

  const schema = z.object({
    email: z.email({ message: "Invalid email address" }),
    password: z.string({ message: "Password is required" }).min(8, { message: "Password must be at least 8 characters" }),
    name: z.string().min(1, { message: "Name is required" }),
    phone: z.string().optional().refine((value) => {
      if (!value) return true;
      if (phoneFieldRef.current) {
        const iti = phoneFieldRef.current.getInstance();
        return iti.isValidNumber();
      }
    }, { message: "Invalid phone number format" }),
    phoneOptIn: z.boolean().optional(),
    acceptTerms: z.boolean().refine((value) => value === true, { message: "You must accept the terms and conditions" }),
  });

  const { control, register, handleSubmit, formState: { errors } } = useForm<SignUpFormData>({
    defaultValues: {
      email: '',
      password: '',
      name: '',
      phone: '',
      phoneOptIn: false,
      acceptTerms: false,
    },
    resolver: zodResolver(schema) as any,
  });

  const onSubmit = (data: SignUpFormData) => {
    mutate(data);
  };

  console.log(errors);

  return (
    <div className="container signup-container">
      <div className="signup-form">
        {isError && (
          <wa-callout variant="danger">
            <i className="fa-regular fa-circle-exclamation" slot="icon"></i>
            <strong>{errorMessage}</strong>
          </wa-callout>
        )}
        {success ? (
          <div className="wa-stack wa-gap-md">
            <wa-callout variant="success">
              <i className="fa-regular fa-circle-check" slot="icon"></i>
              <strong>Sign up successful!</strong>
            </wa-callout>
            <p className="center">Please check your email to verify your account.</p>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <wa-input type="email" label="E-mail" required autofocus {...register('email')} className={errors.email ? 'invalid' : ''} hint={errors.email ? errors.email.message : ""}></wa-input>
              <wa-input type="password" label="Password" required {...register('password')} password-toggle className={errors.password ? 'invalid' : ''} hint={errors.password ? errors.password.message : ""}></wa-input>
              <wa-input type="text" label="Name" required {...register('name')} className={errors.name ? 'invalid' : ''} hint={errors.name ? errors.name.message : ""}></wa-input>
              <label htmlFor="phone" className="label">Phone number</label>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <div className={`intl-tel-input-container ${errors.phone ? 'invalid' : ''}`}>
                    <IntlTelInput
                      countryOrder={["us","ca"]}
                      initialCountry="us"
                      loadUtils={() => import('intl-tel-input/utils')}
                      ref={phoneFieldRef}
                      inputProps={{ id: 'phone' }}
                      onChangeNumber={(number) => {
                        field.onChange(number);
                      }}
                    />
                    {errors.phone && <p className="error-message">{errors.phone.message}</p>}
                  </div>
                )}
              />
              <div className="checkbox-container">
                <label htmlFor="phoneOptIn">
                  <input type="checkbox" id="phoneOptIn" {...register('phoneOptIn')} />
                  <span>I agree to receive SMS notifications</span>
                </label>
              </div>
              <div className="checkbox-container">
                <label htmlFor="acceptTerms">
                  <input type="checkbox" id="acceptTerms" {...register('acceptTerms')} />
                  <span>I agree to the <a href="https://lgcystory.com/privacy-policy/" target="_blank">terms and conditions</a></span>
                  {errors.acceptTerms && <p className="error-message">{errors.acceptTerms.message}</p>}
                </label>
              </div>
              <wa-button type="submit" disabled={isPending}>
                {isPending ? 'Signing up...' : 'Sign Up'}
              </wa-button>
            </form>
            <div className="signup-info">
              Already have an account? <Link to="/login">Login here</Link>.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SignUp;
